import React, { useState, useEffect } from "react";
import { Button, Input, Select, SelectItem, Chip } from "@nextui-org/react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { usersAPI, clientsAPI } from "../services/api";

import DefaultLayout from "@/layouts/default";

interface Client {
  id: string;
  name: string;
  subdomain: string;
}

const EditUserPage: React.FC = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userRole, setUserRole] = useState<string>("");
  const [availableRoles, _setAvailableRoles] = useState<string[]>([
    "admin",
    "user",
    "superadmin",
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [currentUserRole, _setCurrentUserRole] = useState<string>(
    localStorage.getItem("role") || ""
  );
  const [currentUserClientId, _setCurrentUserClientId] = useState<
    string | null
  >(localStorage.getItem("clientId") || null);
  const [clientInfo, setClientInfo] = useState<Client | null>(null);
  const isSuperAdmin = currentUserRole === "superadmin";
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) {
        setError(t("User ID is not provided."));
        setPermissionLoading(false);
        return;
      }

      setLoading(true);
      try {
        const userData = await usersAPI.getById(userId);

        setEmail(userData.email || "");
        setUserRole(userData.role || "");

        // If the user belongs to a client, fetch the client information
        if (userData.clientId) {
          try {
            const client = await clientsAPI.getById(userData.clientId);
            setClientInfo(client);
          } catch (err) {
            console.error("Error fetching client:", err);
          }
        }

        // Check permissions after user data is loaded
        const hasPermission = await checkEditPermissions();
        if (!hasPermission) {
          setError(t("You don't have permission to edit this user."));
        }
        setPermissionLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError(t("Failed to fetch user data."));
        setPermissionLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, t]);

  // Check if current user has permission to edit this user
  const checkEditPermissions = async () => {
    // If current user is a superadmin, they can edit any user
    if (isSuperAdmin) {
      return true;
    }

    // Tenant admins can only edit users in their own client
    if (currentUserRole === "admin" && currentUserClientId) {
      // Get the user we're trying to edit
      try {
        const userData = await usersAPI.getById(userId || "");
        
        // Convert both to strings for comparison to avoid type mismatch
        const userClientId = String(userData.clientId || userData.client_id || "");
        const currentClientId = String(currentUserClientId);
        
        console.log("Permission check:", {
          currentUserRole,
          currentClientId,
          userClientId,
          userData,
          hasPermission: userClientId === currentClientId
        });

        // Check if the user belongs to the same client as the admin
        return userClientId === currentClientId;
      } catch (error) {
        console.error("Error checking permissions:", error);
        return false;
      }
    }

    // Other roles can't edit users
    return false;
  };

  const handleRoleChange = (value: string) => {
    setUserRole(value);
  };

  const handleUpdateUser = async (event: React.FormEvent) => {
    event.preventDefault();

    // Verify permissions
    const hasPermission = await checkEditPermissions();

    if (!hasPermission) {
      setError(t("You don't have permission to edit this user."));

      return;
    }

    try {
      setLoading(true);

      // Prepare update data
      const updateData: { email?: string; role?: string; password?: string } =
        {};

      if (email) {
        updateData.email = email;
      }

      if (userRole) {
        updateData.role = userRole;
      }

      if (password) {
        updateData.password = password;
      }

      // Only proceed if there's something to update
      if (Object.keys(updateData).length === 0) {
        setError(t("No changes to update."));

        return;
      }

      // Update the user
      await usersAPI.update(userId || "", updateData);

      setSuccess(true);
      setTimeout(() => {
        navigate("/users");
      }, 1500);
    } catch (error) {
      console.error("Error updating user:", error);
      setError(t("Failed to update user. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  // If no permission, show access denied
  if (error === t("You don't have permission to edit this user.")) {
    return (
      <DefaultLayout>
        <div className="p-4">
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p className="font-bold">{t("Access Denied")}</p>
            <p>{t("You don't have permission to edit this user.")}</p>
          </div>
          <Button color="primary" onPress={() => navigate("/users")}>
            {t("Back to Users")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  // Show loading while checking permissions or loading user data
  if (permissionLoading || loading) {
    return (
      <DefaultLayout>
        <div className="p-4">
          <p>{t("Loading...")}</p>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">{t("Edit User")}</h1>
          {clientInfo && (
            <Chip color="primary">
              {t("Client")}: {clientInfo.name}
            </Chip>
          )}
        </div>

        <form className="space-y-4" onSubmit={handleUpdateUser}>
          {error && <p className="text-red-500 mb-2">{error}</p>}
          {success && (
            <p className="text-green-500 mb-2">
              {t("User updated successfully!")}
            </p>
          )}

          <Input
            fullWidth
            isClearable
            label={t("Email")}
            placeholder={t("Enter the user's email")}
            type="email"
            value={email}
            variant="bordered"
            onChange={(e) => setEmail(e.target.value)}
          />

          <Input
            fullWidth
            isClearable
            label={t("New Password (leave blank to keep current)")}
            placeholder={t("Enter a new password")}
            type="password"
            value={password}
            variant="bordered"
            onChange={(e) => setPassword(e.target.value)}
          />

          <Select
            className="w-full"
            label={t("Role")}
            placeholder={t("Select role")}
            selectedKeys={[userRole]}
            onSelectionChange={(keys) =>
              handleRoleChange(Array.from(keys)[0] as string)
            }
          >
            {availableRoles.map((role) => (
              <SelectItem key={role} value={role}>
                {role}
              </SelectItem>
            ))}
          </Select>

          <div className="flex gap-2 mt-4">
            <Button
              color="danger"
              variant="flat"
              onPress={() => navigate("/users")}
            >
              {t("Cancel")}
            </Button>
            <Button color="primary" type="submit">
              {t("Update User")}
            </Button>
          </div>
        </form>
      </div>
    </DefaultLayout>
  );
};

export default EditUserPage;
